import { z } from 'zod';

// ============================================
// Menu Reordering
// ============================================

/** Schema for reordering menu items */
export const reorderMenuItemsSchema = z.object({
  itemIds: z.array(z.string().min(1)).min(1),
});

/** Schema for reordering categories */
export const reorderCategoriesSchema = z.object({
  categoryIds: z.array(z.string().min(1)).min(1),
});

/** Schema for reordering modifier groups */
export const reorderModifierGroupsSchema = z.object({
  groupIds: z.array(z.string().min(1)).min(1),
});

/** Schema for reordering modifier group items (options within a group) */
export const reorderModifierGroupItemsSchema = z.object({
  itemIds: z.array(z.string().min(1)).min(1),
});

// ============================================
// Customer Management
// ============================================

/** Schema for blocking a customer */
export const blockCustomerSchema = z.object({
  reason: z.string().max(500).optional(),
});

// ============================================
// Admin Management
// ============================================

const adminRoleSchema = z.enum(['owner', 'admin', 'viewer', 'driver']);

/** Schema for inviting an admin */
export const inviteAdminSchema = z.object({
  telegramId: z.string().min(1),
  role: adminRoleSchema,
});

/** Schema for updating admin role */
export const updateAdminRoleSchema = z.object({
  role: adminRoleSchema,
});

/** Schema for creating an invitation */
export const createInvitationSchema = z.object({
  role: z.enum(['admin', 'viewer', 'driver']),
});

// ============================================
// Modifier Groups
// ============================================

/** Schema for upserting modifier group items (add/update) */
export const upsertModifierGroupItemSchema = z.object({
  menuItemId: z.string().min(1),
  priceOverride: z.number().min(0).optional().nullable(),
  excluded: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

/** Per-group override when assigning to a product */
export const modifierGroupAssignmentSchema = z.object({
  modifierGroupId: z.string().min(1),
  requiredOverride: z.boolean().nullable().optional(),
  minSelectionsOverride: z.number().int().min(0).nullable().optional(),
  maxSelectionsOverride: z.number().int().min(1).nullable().optional(),
  freeSelectionsOverride: z.number().int().min(0).nullable().optional(),
});

/** Schema for assigning modifier groups to a menu item (with optional per-product overrides) */
export const assignModifierGroupsSchema = z.object({
  /** Simple list of group IDs (backwards compatible, no overrides) */
  modifierGroupIds: z.array(z.string().min(1)).optional(),
  /** Detailed assignments with optional per-product overrides */
  assignments: z.array(modifierGroupAssignmentSchema).optional(),
});

// ============================================
// Restaurant Config
// ============================================

const timeSlotSchema = z.object({
  open: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:MM)'),
  close: z.string().regex(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Invalid time format (HH:MM)'),
});

const openingHoursSchema = z.record(
  z.string(),
  z.array(timeSlotSchema).nullable()
);

const feesConfigSchema = z.object({
  taxEnabled: z.boolean().optional(),
  taxRate: z.number().min(0).max(100).optional(),
  taxName: z.string().max(50).optional(),
  serviceFeePercent: z.number().min(0).max(100).optional(),
  serviceFeeFixed: z.number().min(0).optional(),
  serviceFeeName: z.string().max(50).optional(),
  tipsEnabled: z.boolean().optional(),
  tipOptions: z.array(z.number().min(0).max(100)).optional(),
});

const deliveryConfigSchema = z.object({
  fee: z.number().int().min(0), // cents
  minOrder: z.number().int().min(0), // cents
  estimatedTime: z.string().max(50),
  maxRadiusKm: z.number().min(0).max(100).optional(), // km, 0 or undefined = no limit
});

/** Schema for updating restaurant config */
export const updateRestaurantConfigSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  isOpen: z.boolean().optional(),
  openingHours: openingHoursSchema.nullable().optional(),
  closedDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).optional(),
  timezone: z.string().min(1).max(50).optional(),
  pickupEnabled: z.boolean().optional(),
  deliveryEnabled: z.boolean().optional(),
  fees: feesConfigSchema.nullable().optional(),
  deliveryConfig: deliveryConfigSchema.nullable().optional(),
  latitude: z.number().min(-90).max(90).nullable().optional(),
  longitude: z.number().min(-180).max(180).nullable().optional(),
  logoUrl: z.string().url().nullable().optional(),
});

// ============================================
// Type exports (only types NOT defined in types/admin.ts)
// ============================================

export type ReorderMenuItemsInput = z.infer<typeof reorderMenuItemsSchema>;
export type ReorderCategoriesInput = z.infer<typeof reorderCategoriesSchema>;
export type ReorderModifierGroupsInput = z.infer<typeof reorderModifierGroupsSchema>;
export type ReorderModifierGroupItemsInput = z.infer<typeof reorderModifierGroupItemsSchema>;
export type BlockCustomerInput = z.infer<typeof blockCustomerSchema>;
export type CreateInvitationInput = z.infer<typeof createInvitationSchema>;
export type AssignModifierGroupsInput = z.infer<typeof assignModifierGroupsSchema>;

// Note: InviteAdminInput, UpdateAdminRoleInput, UpdateRestaurantConfigInput,
// and UpsertModifierGroupItemInput are already defined in types/admin.ts

